import { isString } from 'lodash';
import React, { CSSProperties } from 'react';
import { Icon } from '@shared/components/Icon';
import { View } from '@shared/components/View';
import { MBListRow, MBListRowProps } from '@shared/components/list/row';
import { Colors, bounceIn } from '@shared/styles';

export interface SelectableRowProps extends MBListRowProps {
  /*
        Is the row currently selected
    */
  selected?: boolean;

  /*
        If set, the selectable dot will be shown as a
        radio button (filled circle). Otherwise a checkmark
        will be shown. Default is true.
    */
  radio?: boolean;

  radioStyle?: CSSProperties;

  /*
        Callback for when the row is pressed and thus selected/deselected
    */
  onChange?: (selected: boolean) => void;
}

export function SelectableRow({
  selected,
  title,
  onChange,
  onPress,
  radio = true,
  radioStyle,
  ...otherProps
}: SelectableRowProps) {
  const dotTitle = isString(title) ? title : undefined;
  const handlePress = React.useCallback(
    () => {
      onChange?.(!selected);
      onPress?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, onPress],
  );

  return (
    <MBListRow
      onPress={handlePress}
      icon={
        <View fill center style={radioStyle}>
          <SelectableRadioDot selected={selected} radio={radio} title={dotTitle} />
        </View>
      }
      title={title}
      {...otherProps}
    />
  );
}

interface SelectableRadioDotProps {
  selected?: boolean;
  radio?: boolean;
  /** Icon size in grid units. */
  gridSize?: number;
  testID?: string;
  title?: string;
}

/*
    A simple radio button dot with different appearance depending on if it's selected or not
*/
export function SelectableRadioDot({
  selected,
  gridSize,
  radio = true,
  testID,
  title,
}: SelectableRadioDotProps) {
  const role = radio ? 'radio' : 'checkbox';
  const color = selected ? Colors.v2.green : Colors.v2.green30;
  const radioIcon = selected ? 'radio-button' : 'unfilled-circle';
  const checkboxIcon = selected ? 'check-box' : 'check-box-empty';
  const icon = radio ? radioIcon : checkboxIcon;

  return (
    <View
      role={role}
      aria-checked={!!selected}
      aria-label={title}
      className={selected && bounceIn}
      key={selected ? 'selected' : 'not-selected'}
    >
      <Icon color={color} name={icon} gridSize={gridSize} testID={testID} />
    </View>
  );
}
